import { useMutation } from "@tanstack/react-query";

import { fetchDataGet } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { logger } from "@/core/utils/func/logger";
import { kycEndpoints } from "@/features/kyc/api/endpoints";
import type { DocumentDownloadUrlDto } from "@/features/kyc/api/dto";

/**
 * Récupère l'URL présignée GET de consultation d'une pièce
 * (`GET .../documents/{id}/download-url`) — réservé aux rôles de revue
 * (flashpay-superadmin, flashpay-admin) : la fuite d'une URL signée équivaut
 * à la fuite du document d'identité (guide §6.2).
 *
 * Mutation (et non query) : l'URL est demandée AU CLIC puis ouverte
 * immédiatement — jamais mise en cache (elle expire, `expiresAt`).
 */
export const useDocumentDownloadUrl = () =>
  useMutation({
    mutationKey: ["kyc", "kyc-documents", "download-url"],
    mutationFn: ({
      kycCaseId,
      documentId,
    }: {
      kycCaseId: string;
      documentId: string;
    }) =>
      fetchDataGet<DocumentDownloadUrlDto>({
        url: kycEndpoints.kycDocuments.downloadUrl(kycCaseId, documentId),
      }),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useDocumentDownloadUrl failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "kyc" }
      );
    },
  });
